import { ReactNode, useRef } from 'react'
import styled from '@emotion/styled'
import { useMediaQuery } from 'usehooks-ts'
import { useRouterState } from '@tanstack/react-router'

import { A, StyledAnchor } from '../A'
import { Flex } from '../Flex'
import SubSideBar from '../SubSidebar'

import SideBarContent from './SideBarContent'

import { Logo, LogoSmall } from '@/assets/icons'
import { ItemConfig } from '@/constants'
import { colors } from '@/constants/colors'
import { asUseMediaQuery, mediaQuery } from '@/utils/mediaQuery'
import { findChild } from '@/utils/subMenu'

const SideBarWrapper = styled.div<{
  sideBarOpen: boolean
  hasSubmenu?: boolean
}>`
  flex-shrink: 0;

  display: grid;
  grid-template-columns: ${({ sideBarOpen, hasSubmenu }) =>
    `${sideBarOpen ? '16.25rem' : '5.25rem'} ${hasSubmenu ? '16.25rem' : ''}`};
  ${mediaQuery('sm')} {
    display: none;
  }
  grid-template-rows: 100vh;

  ${mediaQuery('lg')} {
    grid-template-columns: ${({ sideBarOpen, hasSubmenu }) =>
      `${sideBarOpen ? '5.25rem' : '5.25rem'} ${hasSubmenu ? '16.25rem' : ''}`};
  }

  & ${StyledAnchor} {
    outline: none;
  }
`

const SidebarInner = styled(Flex)<{ sideBarOpen: boolean }>`
  width: ${({ sideBarOpen }) => (sideBarOpen ? '16.25rem' : '5.25rem')};
  flex-direction: column;
  border-right: 0.5px solid ${colors.darkBlue4};
  height: 100vh;
  top: 0;
  position: sticky;
  transition-property: width;
  transition-duration: 250ms;
  will-change: width;
  overflow: hidden;

  &:hover {
    width: 16.25rem;
  }

  ${mediaQuery('lg')} {
    width: 5.25rem;

    &:hover {
      width: 5.25rem;
    }
  }
`

const LogoWithOpener = styled.div`
  padding: 1.375rem 1.875rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
`

export default function SideBar({
  config,
  title,
}: {
  config: ItemConfig[]
  title?: ReactNode
}) {
  const parent = useRef<HTMLDivElement | null>(null)
  const location = useRouterState({ select: (s) => s.location.pathname })

  const current = findChild(location, config)
  const isMd = useMediaQuery(asUseMediaQuery('lg'))

  return (
    <SideBarWrapper sideBarOpen hasSubmenu={!!current?.submenu}>
      <SidebarInner ref={parent} sideBarOpen>
        <LogoWithOpener>
          <A href="/">{isMd ? <LogoSmall /> : <Logo />}</A>
        </LogoWithOpener>
        <SideBarContent config={config} location={location} sideBarOpen />
      </SidebarInner>
      {current?.submenu && (
        <SubSideBar title={title}>
          <SideBarContent
            withIcon={false}
            config={current.submenu}
            location={location}
            isSubSidebar
          />
        </SubSideBar>
      )}
    </SideBarWrapper>
  )
}
